import {
  faChevronDown,
  faChevronRight,
  faChevronUp,
  faUpload as faUploadRegular,
} from '@fortawesome/pro-regular-svg-icons';
import { faUpload, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  ResponsiveTable,
  Text,
  TextButton,
  Tooltip,
} from '@modusign/design-system';
import { getLabelList } from '@modusign/openapi-client-category';
import {
  getGetDocumentListQueryKey,
  getGetExternalDocumentCountQueryKey,
} from '@modusign/openapi-client-external-document';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { Prompt, useHistory } from 'react-router';
import { useToggle } from 'react-use';
import { v4 } from 'uuid';

import dropzoneImage from '@app/assets/external-document-upload.png';
import FileUploadErrorModal from '@app/components/FileUploadErrorModal';
import {
  FileUploadErrorBuilder,
  IFileUploadError,
} from '@app/components/FileUploadErrorModal/fileUploadErrorBuilder';
import InfoCallout from '@app/components/InfoCallout';
import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import LabelSection from '@app/features/externalDocument/components/UploadPage/LabelSection';
import { MAXIMUM_FILE_LIMIT } from '@app/features/externalDocument/constants';
import useExternalDocumentLimit from '@app/features/externalDocument/hooks/useExternalDocumentLimit';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import usePreventUnload from '@app/hooks/usePreventUnload';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { validateAttachmentFile } from '@app/libs/file/validateFile';

import useOrdering from '../../../hooks/useOrdering';
import checkIsContractableFileName from '../../../libs/checkIsContractableFileName';
import { startUpload } from '../../../state';
import truncateDocumentTitle from '../../../utils/truncateDocumentTitle';
import NotAnOriginalDocumentInlineAlert from '../NotAnOriginalDocumentInlineAlert';

type DocumentFile = {
  file: File;
  isContractFile: boolean;
};

type Document = {
  listKey: string;
  name: string;
  files: DocumentFile[];
};

interface MultipleUploadPanelProps {
  showMultipleUploadTooltip: boolean;
}

export default function MultipleUploadPanel({
  showMultipleUploadTooltip,
}: MultipleUploadPanelProps) {
  const history = useHistory();
  const dispatch = useDispatch();
  const client = useQueryClient();
  const workspaceId = useWorkspaceId();

  const { createErrorHandler } = useOpenErrorModal();

  const [documents, setDocuments] = useState<Document[]>([]);
  const [labelIds, setLabelIds] = useState<string[]>([]);

  const { initOrdering } = useOrdering();

  const preventUnload = useRef(!!documents.length);
  preventUnload.current = !!documents.length;
  usePreventUnload(preventUnload.current);

  const getLabelsEnsureLabelId = async () => {
    if (labelIds.length === 0) {
      return [];
    }
    const { labels } = await getLabelList(workspaceId, { labelIds });
    return labels;
  };

  const { mutate, isLoading } = useMutation(
    async () => {
      const labels = await getLabelsEnsureLabelId();
      dispatch(
        startUpload({
          documents: documents.map((d) => ({
            name: d.name,
            files: d.files.map((f) => ({
              file: f.file,
              name: f.file.name,
              isContract: f.isContractFile,
            })),
            metadata: { labels },
          })),
        })
      );
      initOrdering();
      preventUnload.current = false;
      history.push(`/external-documents/list`);
    },
    {
      onSuccess: () => {
        client.invalidateQueries(getGetDocumentListQueryKey(workspaceId));
        client.invalidateQueries(getGetExternalDocumentCountQueryKey(workspaceId));
      },
      onError: createErrorHandler({ title: '계약 등록 실패' }),
    }
  );

  return (
    <>
      <Prompt
        message={() => {
          if (preventUnload.current) {
            return '지금 이 페이지를 벗어날 경우, 진행 중인 작업이 중단됩니다.';
          }
          return true;
        }}
      />
      {isLoading && <GlobalLoadingSpinner />}
      <Heading as="h2" textAlign="center">
        여러개 일괄 등록
      </Heading>
      <Text
        as="div"
        color="textNeutralLight"
        sx={{
          pb: 8,
          mt: 3,
        }}
        textAlign="center"
      >
        계약이 여러개 들어있는 폴더를 업로드하여, 일괄로 등록할 수 있습니다.
        <br />
        등록 이후 [계약 관리]에서 엑셀을 이용해 정보를 일괄 업데이트할 수 있습니다.
      </Text>

      <TutorialLink showMultipleUploadTooltip={showMultipleUploadTooltip} />

      <FileUploadDropzone documents={documents} setDocuments={setDocuments} />
      <LabelSection index={2} labelIds={labelIds} onLabelChange={setLabelIds} />
      <Box textAlign="center">
        <Tooltip
          content="폴더를 업로드해 주세요"
          isOpen={documents.length === 0 ? undefined : false}
          placement="top"
        >
          <Button
            disabled={documents.length === 0}
            height="42px"
            mt="66px"
            variant="primary"
            width="180px"
            onClick={() => mutate()}
          >
            등록하기
          </Button>
        </Tooltip>
      </Box>
      <NotAnOriginalDocumentInlineAlert sx={{ mt: '54px' }} />
    </>
  );
}

// TODO(org3): org3 배포 후 튜토리얼 영상이 만들어지면 다시 TutorialVideoAccordion으로 바꿀 예정입니다. [QA-530]
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function TutorialLink({ showMultipleUploadTooltip }: { showMultipleUploadTooltip: boolean }) {
  return (
    <Flex alignItems="center" sx={{ gap: 6 }}>
      <Box bg="dividerNeutralLight" flex="auto" height="1px" />
      <Flex
        alignItems="center"
        as="a"
        border="1px solid"
        borderColor="buttonBorderNeutralBase"
        borderRadius="16px"
        color="textNeutralLight"
        fontSize="12px"
        href="https://support.modusign.co.kr/hc/ko/articles/7658081676953"
        lineHeight={1.5}
        px={5}
        py={2}
        target="_blank"
      >
        등록 방법 자세히 보기
        <FontAwesomeIcon icon={faChevronRight} style={{ marginLeft: '3px' }} />
      </Flex>
      <Box bg="dividerNeutralLight" flex="auto" height="1px" />
    </Flex>
  );
}

// TODO(org3): org3 배포 후 튜토리얼 영상이 만들어지면 다시 사용할 예정입니다. [QA-530]
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function TutorialVideoAccordion({
  showMultipleUploadTooltip,
}: {
  showMultipleUploadTooltip: boolean;
}) {
  const [isOpenTutorial, toggleIsOpenTutorial] = useToggle(false);

  return (
    <>
      <Box
        as="section"
        hidden={!isOpenTutorial}
        mb={6}
        position="relative"
        pt="56.25%"
        width="100%"
      >
        <iframe
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          frameBorder="0"
          src="https://www.youtube.com/embed/mLJgJzUupEU"
          style={{ width: '100%', height: '100%', position: 'absolute', left: 0, top: 0 }}
          title="외부문서 일괄 등록 이용 안내"
        />
      </Box>
      <Flex alignItems="center" sx={{ gap: 6 }}>
        <Box bg="dividerNeutralLight" flex="auto" height="1px" />
        <Tooltip
          content={
            <Text
              as="p"
              color="textNeutralLightest"
              fontSize="xs"
              fontWeight="medium"
              textAlign="center"
            >
              일괄 등록이 처음이신가요?
              <br />
              튜토리얼 영상을 보며 진행해 보세요.
            </Text>
          }
          isOpen={showMultipleUploadTooltip}
          placement="bottom"
        >
          <Flex
            alignItems="center"
            as="button"
            border="1px solid"
            borderColor="buttonBorderNeutralBase"
            borderRadius="16px"
            color="textNeutralLight"
            fontSize="12px"
            lineHeight={1.5}
            px={5}
            py={2}
            onClick={toggleIsOpenTutorial}
          >
            <FontAwesomeIcon
              icon={isOpenTutorial ? faChevronUp : faChevronDown}
              style={{ marginRight: '3px' }}
            />
            {isOpenTutorial ? '접기' : '튜토리얼 영상 보기'}
          </Flex>
        </Tooltip>
        <Box bg="dividerNeutralLight" flex="auto" height="1px" />
      </Flex>
    </>
  );
}

export function aggregateDocument(files: Array<File & { path: string }>): Array<{
  name: string;
  files: File[];
}> {
  const documentMap = new Map<string, Array<File & { path: string }>>();

  files.forEach((f) => {
    if (f.name === '.DS_Store') {
      return;
    }

    // 디렉토리 안의 파일이 아닌 경우 각 파일을 문서로 취급합니다.
    if (!f.path.includes('/')) {
      const base = f.name.substring(0, f.name.lastIndexOf('.'));
      documentMap.set(base, [f]);
      return;
    }

    let { path } = f;
    if (path.startsWith('/')) {
      path = path.slice(1);
    }
    const documentName = truncateDocumentTitle(
      path
        .slice(0, path.lastIndexOf('/'))
        .normalize('NFC')
        .split('/')
        .map((v) => v.replace(/:/g, '/'))
    );
    const value = documentMap.get(documentName) || [];
    value.push(f);
    documentMap.set(documentName, value);
  });

  return Array.from(documentMap.entries()).flatMap(([key, value]) => {
    return [
      {
        name: key,
        files: value,
      },
    ];
  });
}
/**
 * 파일 배열을 받아 다음 유효성 검사가 진행된 파일 배열을 반환합니다.
 * 1. 파일 크기 10 MB 이하인지 확인
 * 2. 파일 확장자 체크
 * 3. 파일 개수 체크
 * 4. 가능한 첨부 파일이 있는지 체크
 * @param fileList
 * @returns
 */

async function getValidatedFiles(documentName: string, fileList: File[]) {
  let hasContractFile = false;

  const validatingFiles = fileList
    .map((file) => {
      const errorBuilder = new FileUploadErrorBuilder(file.name);
      errorBuilder.setDocumentName(documentName);
      let canBeContractFile = checkIsContractableFileName(file.name);
      // 파일 크기, 계약서로 설정 가능한지 체크
      if (file.size > 10 * 1000 * 1000) {
        errorBuilder.fileSizeExceededError(10 * 1000 * 1000);
        canBeContractFile = false;
      }
      return {
        file,
        errorBuilder,
        canBeContractFile,
      };
    })
    .sort((a, b) => {
      if (!a.canBeContractFile && b.canBeContractFile) return 1;
      if (a.canBeContractFile && !b.canBeContractFile) return -1;
      return 0;
    })
    .map(({ file, canBeContractFile, errorBuilder }, idx) => {
      // 가능한 첫번째 파일을 계약서로 설정합니다.
      const isContractFile = canBeContractFile && idx === 0;
      if (isContractFile) {
        hasContractFile = true;
      }

      if (!hasContractFile) {
        errorBuilder.noValidContractFileError();
      }

      return {
        file,
        errorBuilder,
        isContractFile,
      };
    })
    .sort((a, b) => {
      if (a.errorBuilder.currentErrorsCount === b.errorBuilder.currentErrorsCount) return 0;
      if (a.errorBuilder.currentErrorsCount > b.errorBuilder.currentErrorsCount) return 1;
      return -1;
    })
    .map((f, idx) => {
      if (idx + 1 > MAXIMUM_FILE_LIMIT) {
        f.errorBuilder.fileNumberExceededError(MAXIMUM_FILE_LIMIT);
      }
      return { file: f.file, isContractFile: f.isContractFile, errorBuilder: f.errorBuilder };
    });

  await Promise.all(
    validatingFiles.map(async (f) => {
      const isInvalidExtension = !(await validateAttachmentFile(f.file));
      if (isInvalidExtension) {
        f.errorBuilder.fileExtensionError();
      }
    })
  );

  return validatingFiles.map((f) => ({
    file: f.file,
    isContractFile: f.isContractFile,
    errors: f.errorBuilder.build(),
  }));
}

// TODO: 테스트 작성
/**
 *
 * @param files 파일 배열
 * @param documentName 문서 이름
 */
async function validateFiles(
  files: File[],
  documentName: string
): Promise<{ accepted: DocumentFile[]; rejected: IFileUploadError[] }> {
  const validatedFiles: Array<{
    file: File;
    errors: IFileUploadError | null;
    isContractFile: boolean;
  }> = await getValidatedFiles(documentName, files);

  const accepted = validatedFiles
    .filter((f) => !f.errors)
    .map((f) => ({ file: f.file, isContractFile: f.isContractFile }));

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const rejected = validatedFiles.filter((f) => f.errors).map((f) => f.errors!);

  return { accepted, rejected };
}

interface FileUploadDropzoneProps {
  documents: Document[];
  setDocuments: Dispatch<SetStateAction<Document[]>>;
}

function FileUploadDropzone({ documents, setDocuments }: FileUploadDropzoneProps) {
  const limit = useExternalDocumentLimit();
  const [isValidating, setIsValidating] = useState(false);

  const [errors, setErrors] = useState<IFileUploadError[] | null>(null);

  const { getRootProps, getInputProps, isDragActive, open, inputRef } = useDropzone({
    noClick: true,
    multiple: true,
    useFsAccessApi: false,
    onDropAccepted: async (params) => {
      try {
        setIsValidating(true);
        const newFiles = params as unknown as Array<File & { path: string }>;
        const newDocuments = aggregateDocument(newFiles);

        const acceptedDocuments: Document[] = [];
        const rejectedFileErrors: IFileUploadError[] = [];

        await Promise.all(
          newDocuments.map(async (document) => {
            // eslint-disable-next-line no-await-in-loop
            const { accepted, rejected } = await validateFiles(document.files, document.name);

            rejectedFileErrors.push(...rejected);
            acceptedDocuments.push({
              name: document.name,
              listKey: v4(),
              files: accepted,
            });
          })
        );

        setDocuments((documents) =>
          documents.concat(acceptedDocuments.filter((document) => document.files.length > 0))
        );
        if (rejectedFileErrors.length > 0) {
          setErrors(rejectedFileErrors);
        }
      } finally {
        setIsValidating(false);
      }
    },
  });

  useEffect(() => {
    if (inputRef.current !== null) {
      inputRef.current.setAttribute('directory', '');
      inputRef.current.setAttribute('webkitdirectory', '');
    }
  }, [inputRef]);

  return (
    <Box as="section" borderBottom="1px solid" borderBottomColor="dividerNeutralLight" pb={7}>
      <FileUploadErrorModal errors={errors} onConfirm={() => setErrors(null)} />
      <Flex alignItems="center" justifyContent="space-between" my={8} width="100%">
        <Flex alignItems="center" width="200px">
          <Flex
            alignItems="center"
            bg="statusBackgroundPrimaryBase"
            borderRadius="50%"
            fontSize="10px"
            fontWeight="bold"
            height="21px"
            justifyContent="center"
            width="21px"
          >
            1
          </Flex>
          <Heading as="h3" flex="auto" fontSize="md" ml={3}>
            폴더 업로드
          </Heading>
        </Flex>
        {documents.length > 0 && (
          <TextButton color="textNeutralLight" onClick={open}>
            <FontAwesomeIcon icon={faUploadRegular} /> 업로드
          </TextButton>
        )}
      </Flex>
      <Box
        {...getRootProps()}
        borderColor="boxBorderNeutralBase"
        borderStyle={documents.length ? 'solid' : 'dashed'}
        borderWidth="1px"
        height="480px"
        overflow="auto"
      >
        <input
          {...getInputProps()}
          // input element type에 directory와 webkitdirectory가 없어 타입스크립트를 비활성화 합니다.
          // https://stackoverflow.com/questions/63809230/reactjs-directory-selection-dialog-not-working
          // https://qiita.com/minorin22/items/0a8354a81039ea3700a2
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          /* @ts-expect-error */
          // eslint-disable-next-line react/no-unknown-property
          directory="true"
          webkitdirectory="true"
        />
        {isDragActive && (
          <Flex
            alignItems="center"
            bg="boxBackgroundEmphasisBase"
            border="1px dashed"
            borderColor="boxBorderEmphasisBase"
            color="textEmphasisBase"
            fontWeight="medium"
            height="100%"
            justifyContent="center"
          >
            여기에 파일을 놓아 주세요.
          </Flex>
        )}
        {!isDragActive &&
          (documents.length > 0 ? (
            <ResponsiveTable.Table>
              <ResponsiveTable.THead>
                <ResponsiveTable.THeadRow bg="tableBackgroundNeutralSlightlyDark">
                  <ResponsiveTable.TH>계약 제목</ResponsiveTable.TH>
                  <ResponsiveTable.TH sx={{ width: '120px', textAlign: 'center' }}>
                    파일 개수
                  </ResponsiveTable.TH>
                  <ResponsiveTable.TH sx={{ width: '60px' }} />
                </ResponsiveTable.THeadRow>
              </ResponsiveTable.THead>
              <ResponsiveTable.TBody>
                {documents.map((document) => (
                  <ResponsiveTable.TBodyRow key={document.listKey}>
                    <ResponsiveTable.TD>{document.name}</ResponsiveTable.TD>
                    <ResponsiveTable.TD textAlign="center">
                      {document.files.length}
                    </ResponsiveTable.TD>
                    <ResponsiveTable.TD textAlign="center">
                      <button
                        aria-label="계약 제거하기"
                        type="button"
                        onClick={() =>
                          setDocuments((documents) => documents.filter((d) => d !== document))
                        }
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </ResponsiveTable.TD>
                  </ResponsiveTable.TBodyRow>
                ))}
              </ResponsiveTable.TBody>
            </ResponsiveTable.Table>
          ) : (
            <Flex
              alignItems="center"
              bg="boxBackgroundNeutralSlightlyLight"
              flexDirection="column"
              height="100%"
              justifyContent="center"
              width="100%"
            >
              <Image src={dropzoneImage} />
              <Text fontWeight="medium" mt={9}>
                계약이 여러개 들어있는 폴더를 업로드해 주세요.
              </Text>
              <Text mt={1}>
                {limit.limit === Infinity
                  ? '자동으로 계약이 만들어집니다.'
                  : `업로드 가능 파일 : ${(limit.limit - limit.usage).toLocaleString()}개`}
              </Text>
              <Button fontSize="14px" isLoading={isValidating} mt={6} py={3} onClick={open}>
                <FontAwesomeIcon icon={faUpload} style={{ fontSize: '11px', marginRight: '3px' }} />
                업로드하기
              </Button>
            </Flex>
          ))}
      </Box>
      {documents.length > 0 && (
        <InfoCallout sx={{ mt: 4 }}>총 {documents.length}건의 계약이 등록됩니다.</InfoCallout>
      )}
    </Box>
  );
}
